// client/src/components/reports/BoardReport.jsx
//
// /:tenant/reports/board/:templateKey — board-ready reports (Monthly board
// update, Event recap, Campaign results). Each is several sections that
// combine social results with event and fundraising results. Pick the period
// / event / campaign, read it on screen, export one PDF or CSV, email it on a
// schedule (also to people outside the account) or share a read-only link.
//
// API: POST /api/reports/preview { templateKey, inputs } → { kind:'composite',
// subtitle, sections: [{ key, title, description, skipped, error, result }] }.
// Exports / schedules / shares need the template's tenant row (materialize).

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome, FaRegClock, FaShareAlt } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import ReportResult from './ReportResult';
import ExportBar from './ExportBar';
import ScheduleModal from './ScheduleModal';
import ShareModal from './ShareModal';
import {
  listReports, getReportPermissions, getInputOptions, previewReport, materializeTemplate, exportBoardReport, errorText,
} from '../../services/reportsApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Reports.css';

export const PERIOD_KEYS = ['last_month', 'this_month', 'last_quarter', 'this_quarter', 'year_to_date', 'last_year', 'last_30_days', 'last_90_days'];

function usePeriodLabels(t) {
  return {
    last_month: t('reports.board.period.last_month', 'Last month'),
    this_month: t('reports.board.period.this_month', 'This month'),
    last_quarter: t('reports.board.period.last_quarter', 'Last quarter'),
    this_quarter: t('reports.board.period.this_quarter', 'This quarter'),
    year_to_date: t('reports.board.period.year_to_date', 'Year to date'),
    last_year: t('reports.board.period.last_year', 'Last year'),
    last_30_days: t('reports.board.period.last_30_days', 'Last 30 days'),
    last_90_days: t('reports.board.period.last_90_days', 'Last 90 days'),
  };
}

/** Are all the inputs this template needs filled in? */
export function inputsReady(template, inputs) {
  if (!template) return false;
  return (template.inputs || []).every((k) => (k === 'period' ? !!inputs.period : k === 'event' ? !!inputs.eventId : !!inputs.campaignId));
}

export default function BoardReport({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);
  const { templateKey } = params;
  const periodLabels = usePeriodLabels(t);

  const [template, setTemplate] = useState(null);
  const [perms, setPerms] = useState(null);
  const [options, setOptions] = useState({ event: null, campaign: null });
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [runError, setRunError] = useState('');
  const [running, setRunning] = useState(false);
  const [modal, setModal] = useState(null); // { kind: 'schedule'|'share', id }
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const [list, p] = await Promise.all([listReports(), getReportPermissions()]);
      const tpl = (list.templates || []).find((x) => x.key === templateKey && x.kind === 'composite');
      if (!tpl) { setLoadError(t('reports.board.notFound', 'This report is not available on your account.')); return; }
      setTemplate(tpl);
      setPerms(p.permissions || list.permissions || {});
      const next = {};
      if ((tpl.inputs || []).includes('period')) next.period = tpl.defaultPeriod || 'last_month';
      setInputs(next);
      for (const kind of ['event', 'campaign']) {
        if ((tpl.inputs || []).includes(kind)) {
          const r = await getInputOptions(kind).catch(() => ({ options: [] }));
          setOptions((o) => ({ ...o, [kind]: r.options || [] }));
        }
      }
    } catch (err) {
      setLoadError(errorText(err, t('reports.loadError', 'Could not load reports.')));
    }
  }, [templateKey, t]);

  useEffect(() => { load(); }, [load]);

  const ready = inputsReady(template, inputs);

  useEffect(() => {
    if (!ready) { setResult(null); return undefined; }
    let cancelled = false;
    setRunning(true);
    setRunError('');
    previewReport({ templateKey, inputs })
      .then((r) => { if (!cancelled) setResult(r); })
      .catch((err) => { if (!cancelled) setRunError(errorText(err, t('reports.board.runError', 'Could not run this report.'))); })
      .finally(() => { if (!cancelled) setRunning(false); });
    return () => { cancelled = true; };
  }, [ready, templateKey, inputs, t]);

  const rowId = async () => (await materializeTemplate(templateKey)).report.id;

  const open = async (kind) => {
    setNotice('');
    try {
      setModal({ kind, id: await rowId() });
    } catch (err) {
      setNotice(errorText(err, t('reports.board.openError', 'Could not open this. Try again.')));
    }
  };

  const doExport = async (format) => exportBoardReport(await rowId(), format, inputs);

  const footer = (
    <AppFooter
      buttons={[
        { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(`/${tenant}/reports`) },
        { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
      ]}
    />
  );

  if (loadError) return <div className="rp-page"><ErrorState message={loadError} onRetry={load} />{footer}</div>;
  if (!template || !perms) return <div className="rp-page"><LoadingState />{footer}</div>;

  const pickers = template.inputs || [];

  return (
    <div className="rp-page" data-testid="board-report">
      <section className="surface">
        <div className="rp-head">
          <h2>{template.name}</h2>
          <div className="rp-actions">
            {perms.schedule && (
              <button type="button" className="lm-btn secondary" onClick={() => open('schedule')} disabled={!ready}>
                <FaRegClock aria-hidden="true" /> {t('reports.builder.schedule', 'Schedule')}
              </button>
            )}
            {perms.share && (
              <button type="button" className="lm-btn secondary" onClick={() => open('share')} disabled={!ready}>
                <FaShareAlt aria-hidden="true" /> {t('reports.builder.share', 'Share')}
              </button>
            )}
          </div>
        </div>
        <p className="surface-muted perf-small">{template.description}</p>

        <div className="fo-form-grid">
          {pickers.includes('period') && (
            <label className="fo-field" htmlFor="rp-board-period">
              {t('reports.board.period.label', 'Period')}
              <select id="rp-board-period" value={inputs.period || ''} onChange={(e) => setInputs({ ...inputs, period: e.target.value })}>
                {PERIOD_KEYS.map((k) => <option key={k} value={k}>{periodLabels[k]}</option>)}
              </select>
            </label>
          )}
          {['event', 'campaign'].filter((k) => pickers.includes(k)).map((kind) => {
            const key = kind === 'event' ? 'eventId' : 'campaignId';
            const list = options[kind];
            return (
              <label key={kind} className="fo-field" htmlFor={`rp-board-${kind}`}>
                {kind === 'event' ? t('reports.board.event', 'Event') : t('reports.board.campaign', 'Campaign')}
                <select id={`rp-board-${kind}`} value={inputs[key] || ''} onChange={(e) => setInputs({ ...inputs, [key]: Number(e.target.value) || undefined })} disabled={!list}>
                  <option value="">{kind === 'event' ? t('reports.board.chooseEvent', 'Choose an event…') : t('reports.board.chooseCampaign', 'Choose a campaign…')}</option>
                  {(list || []).map((o) => <option key={o.id} value={o.id}>{o.name}{o.date ? ` (${o.date})` : ''}</option>)}
                </select>
              </label>
            );
          })}
        </div>
        {pickers.some((k) => k !== 'period' && options[k] && options[k].length === 0) && (
          <p className="surface-muted perf-small">
            {pickers.includes('event')
              ? t('reports.board.noEvents', 'There are no events yet.')
              : t('reports.board.noCampaigns', 'There are no campaigns yet. A campaign is created with each new event.')}
          </p>
        )}
        {notice && <div className="note error" role="alert">{notice}</div>}
      </section>

      {ready && perms.export && (
        <section className="surface">
          <ExportBar onExport={doExport} />
        </section>
      )}

      {running && <p className="rp-status" role="status">{t('reports.builder.running', 'Running…')}</p>}
      {runError && <div className="note error" role="alert">{runError}</div>}

      {result && (
        <>
          {result.subtitle && <p className="surface rp-board-subtitle"><strong>{result.subtitle}</strong></p>}
          {result.sections.map((s) => (
            <section key={s.key} className="surface rp-board-section" aria-labelledby={`rp-sec-${s.key}`}>
              <h4 id={`rp-sec-${s.key}`}>{s.title}</h4>
              {s.description && <p className="surface-muted perf-small">{s.description}</p>}
              {s.skipped && <div className="note info">{s.skipped}</div>}
              {s.error && <div className="note error">{s.error}</div>}
              {s.result && <ReportResult result={s.result} kpiOnly />}
            </section>
          ))}
          <p className="surface-muted perf-small rp-board-foot">
            {t('reports.board.more', 'Need something different?')} <Link to={`/${tenant}/reports/new`}>{t('reports.board.build', 'Build your own report')}</Link>
          </p>
        </>
      )}

      {modal && modal.kind === 'schedule' && (
        <ScheduleModal reportId={modal.id} reportName={template.name} canShare={!!perms.share} inputs={inputs} onClose={() => setModal(null)} />
      )}
      {modal && modal.kind === 'share' && (
        <ShareModal reportId={modal.id} reportName={template.name} inputs={inputs} onClose={() => setModal(null)} />
      )}
      {footer}
    </div>
  );
}
